import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faCircleCheck, faCircleXmark } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import {
  PlayerFieldStatus,
  ScoreStatus,
} from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Bench } from '@sorare/core/src/atoms/icons/Bench';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { isType } from '@sorare/core/src/lib/gql';
import { shortStatusMessages } from '@sorare/core/src/lib/players';

import { isGameScheduled } from 'shared/game/lib';

import {
  PlayerGameStatsPrediction,
  ShapeContainer,
} from '../PlayerGameStatsPrediction';
import type { ScheduledPlayerGameScoreState_playerGameScore } from './__generated__/index.graphql';

type Props = {
  variant: 'chip' | 'cornerBadge' | 'text' | 'column-text';
  includesIcon?: boolean;
  playerGameScores: ScheduledPlayerGameScoreState_playerGameScore[];
  hidePredictions?: boolean;
};

const Wrapper = styled.div`
  display: inline-block;
`;

export const ScheduledPlayerGameScoreState = ({
  playerGameScores,
  variant,
  includesIcon,
  hidePredictions,
}: Props) => {
  const scheduledPlayerGameScores = playerGameScores
    .filter(({ anyGame }) => isGameScheduled(anyGame.status))
    .filter(pgs => isType(pgs.anyPlayerGameStats, 'PlayerGameStats'));

  const scheduledPlayerGameScore = scheduledPlayerGameScores[0];

  if (
    !scheduledPlayerGameScore ||
    !isType(scheduledPlayerGameScore.anyPlayerGameStats, 'PlayerGameStats')
  )
    return null;

  if (
    scheduledPlayerGameScore.scoreStatus === ScoreStatus.REVIEWING &&
    scheduledPlayerGameScore.anyPlayerGameStats?.fieldStatus ===
      PlayerFieldStatus.ON_FIELD
  ) {
    return (
      <Wrapper
        className={classNames({
          'max-score-glow': variant === 'chip' || variant === 'cornerBadge',
        })}
      >
        <ShapeContainer variant={variant} includesIcon color="var(--c-white)">
          <Horizontal gap={0.5}>
            <FontAwesomeIcon icon={faCircleCheck} />
            <FormattedNumber value={1} style="percent" />
          </Horizontal>
        </ShapeContainer>
      </Wrapper>
    );
  }

  if (
    scheduledPlayerGameScore.scoreStatus === ScoreStatus.PENDING &&
    scheduledPlayerGameScore.anyPlayerGameStats?.fieldStatus ===
      PlayerFieldStatus.ON_BENCH
  ) {
    return (
      <Wrapper>
        <ShapeContainer
          Icon={Bench}
          variant={variant}
          includesIcon
          color="var(--c-yellow-600)"
        >
          <FormattedNumber value={0} style="percent" />
        </ShapeContainer>
      </Wrapper>
    );
  }

  if (
    scheduledPlayerGameScore.anyPlayerGameStats?.fieldStatus ===
    PlayerFieldStatus.NOT_ON_GAME_SHEET
  ) {
    return (
      <Wrapper>
        <ShapeContainer
          variant={variant}
          includesIcon
          color="var(--c-onBase-error)"
        >
          <Horizontal gap={0.5}>
            <FontAwesomeIcon icon={faCircleXmark} />
            <FormattedMessage
              {...shortStatusMessages[ScoreStatus.DID_NOT_PLAY]}
            />
          </Horizontal>
        </ShapeContainer>
      </Wrapper>
    );
  }
  if (
    isType(scheduledPlayerGameScore.anyPlayerGameStats, 'PlayerGameStats') &&
    scheduledPlayerGameScore.anyPlayerGameStats?.footballPlayingStatusOdds &&
    !hidePredictions
  ) {
    return (
      <PlayerGameStatsPrediction
        playingStatusOdds={
          scheduledPlayerGameScore.anyPlayerGameStats?.footballPlayingStatusOdds
        }
        variant={variant}
        includesIcon={includesIcon}
      />
    );
  }

  return null;
};

ScheduledPlayerGameScoreState.fragments = {
  playerGameScore: gql`
    fragment ScheduledPlayerGameScoreState_playerGameScore on PlayerGameScoreInterface {
      id
      scoreStatus
      anyGame {
        id
        status: statusTyped
      }
      anyPlayerGameStats {
        id
        ... on PlayerGameStats {
          id
          fieldStatus
          footballPlayingStatusOdds {
            ...PlayerGameStatsPrediction_playingStatusOdds
          }
        }
      }
    }
    ${PlayerGameStatsPrediction.fragments.playingStatusOdds}
  ` as TypedDocumentNode<ScheduledPlayerGameScoreState_playerGameScore>,
};
